import { formatDualQty, formatUnitQty, toPrimaryQty, type UnitConversionMaterial } from "@apv-erp/status";
import { cn } from "@/lib/utils";

/**
 * Ô số lượng có chọn ĐVT (chính / phụ) cho dòng chứng từ vật tư (04/11/2026).
 * Số người dùng gõ theo ĐVT đang chọn; dòng dưới hiện số quy đổi ra ĐVT chính (máy chủ tính lại khi lưu).
 * Vật tư không có ĐVT phụ → chỉ hiện ĐVT chính, không có ô chọn.
 */
export interface DualUnitQtyInputProps {
  value: number | string | null | undefined;
  /** ĐVT đang nhập; rỗng = ĐVT chính */
  entryUnit?: string | null;
  material?: UnitConversionMaterial | null;
  onChange: (qty: number, entryUnit: string | null) => void;
  className?: string;
  inputClassName?: string;
  testId?: string;
  min?: number;
  disabled?: boolean;
}

export function DualUnitQtyInput({
  value,
  entryUnit,
  material,
  onChange,
  className,
  inputClassName,
  testId,
  min,
  disabled,
}: DualUnitQtyInputProps) {
  const primary = material?.unit || "";
  const secondary = material?.secondaryUnit && Number(material?.secondaryFactor) > 0 ? material.secondaryUnit : "";
  const current = entryUnit && entryUnit === secondary ? secondary : primary;
  const qty = value === "" || value == null ? NaN : Number(value);
  const converted = secondary && current === secondary && Number.isFinite(qty) ? toPrimaryQty(qty, current, material!) : null;

  return (
    <div className={cn("space-y-0.5", className)}>
      <div className="flex rounded-md border border-slate-200 shadow-sm focus-within:ring-1 focus-within:ring-slate-950 bg-white">
        <input
          type="number"
          inputMode="decimal"
          min={min}
          disabled={disabled}
          data-testid={testId}
          aria-label="Số lượng"
          className={cn(
            "h-9 w-full min-w-0 rounded-l-md bg-transparent px-2 py-1 text-sm text-right focus-visible:outline-none",
            inputClassName,
          )}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? (NaN as any) : Number(e.target.value), current === primary ? null : current)}
        />
        {secondary ? (
          <select
            aria-label="Đơn vị nhập"
            data-testid={testId ? `${testId}-unit` : undefined}
            disabled={disabled}
            className="h-9 shrink-0 rounded-r-md border-l border-slate-200 bg-slate-50 px-1.5 text-xs font-medium text-slate-700 focus-visible:outline-none"
            value={current}
            onChange={(e) => {
              // Đổi ĐVT giữ nguyên lượng hàng: 1.000 Tờ → 2 Ram (không giữ số 1.000 rồi hiểu thành 1.000 Ram)
              const next = e.target.value === primary ? null : e.target.value;
              const f = Number(material?.secondaryFactor);
              const nextQty = Number.isFinite(qty) && f > 0 ? Math.round((next ? qty / f : qty * f) * 1000) / 1000 : qty;
              onChange(nextQty, next);
            }}
          >
            <option value={primary}>{primary}</option>
            <option value={secondary}>{secondary}</option>
          </select>
        ) : primary ? (
          <span className="flex h-9 shrink-0 items-center rounded-r-md border-l border-slate-200 bg-slate-50 px-2 text-xs text-slate-500">
            {primary}
          </span>
        ) : null}
      </div>
      {converted !== null ? (
        <div className="text-right text-[11px] text-slate-500" data-testid={testId ? `${testId}-converted` : undefined}>
          = {formatUnitQty(converted)} {primary}
        </div>
      ) : null}
    </div>
  );
}

/** Hiển thị số lượng dòng chứng từ: "10 Ram (= 5.000 Tờ)" khi nhập theo ĐVT phụ, không thì "5.000 Tờ" */
export function formatEntryLineQty(
  line: { quantity?: any; entryUnit?: string | null; entryQty?: any },
  material?: UnitConversionMaterial | null,
): string {
  const qty = Number(line.quantity) || 0;
  if (line.entryUnit && line.entryQty != null) {
    return `${formatUnitQty(Number(line.entryQty))} ${line.entryUnit} (= ${formatUnitQty(qty)}${material?.unit ? ` ${material.unit}` : ""})`;
  }
  return material ? formatDualQty(qty, material) : formatUnitQty(qty);
}
